/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useManagerStaff.ts
 * PURPOSE: TanStack Query hooks cho màn Nhân viên — Release v0 WP-4 / SC-012-MGR-STAFF
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-29
 * COMPLIANCE: SDLC 6.3.1 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Đội ngũ chi nhánh (Manager)
 * - Impact: Revenue Critical — xem đội ngũ, trạng thái hôm nay, ca tuần từng NV
 * - Stakeholders: Branch Managers, Shift Managers
 *
 * @technical-architecture
 * - Layer: Custom Hooks (Business Logic)
 * - Pattern: TanStack Query v5 (useQuery) — màn CHỈ ĐỌC, không mutation
 * - Dependencies: @tanstack/react-query, managerApi
 *
 * @cross-references
 * - API Service: frontend/src/services/api/managerApi.ts (getManagerStaff + getStaffWeekDetail)
 * - Backend: backend/app/api/v2/endpoints/manager_staff.py
 * - Page: frontend/src/pages/manager/ManagerStaffPage.tsx
 * - Sibling pattern: frontend/src/hooks/useManagerAttendance.ts
 * ===============================================================================
 */

import { useQuery } from "@tanstack/react-query";
import { managerApi } from "../services/api/managerApi";

export const MANAGER_STAFF_QUERY_KEYS = {
  all: ["manager", "staff"] as const,
  list: (date: string) => ["manager", "staff", date] as const,
  weekDetail: (userId: string) => ["manager", "staff", "week-detail", userId] as const,
};

/** Bảng [5] danh sách đội ngũ + trạng thái hôm nay (mặc định hôm nay) */
export function useManagerStaff(dateISO?: string) {
  return useQuery({
    queryKey: MANAGER_STAFF_QUERY_KEYS.list(dateISO ?? ""),
    queryFn: () => managerApi.getManagerStaff(dateISO),
    staleTime: 30_000,
    retry: 2,
  });
}

/** Modal [D] Chi tiết NV: ca tuần này + chấm công hôm nay (chỉ fetch khi mở modal) */
export function useStaffWeekDetail(userId: string | null) {
  return useQuery({
    queryKey: MANAGER_STAFF_QUERY_KEYS.weekDetail(userId ?? ""),
    queryFn: () => managerApi.getStaffWeekDetail(userId as string),
    enabled: !!userId,
    staleTime: 30_000,
    retry: 2,
  });
}
